import { Link, useCanGoBack, useNavigate, useParams, useRouter } from '@tanstack/react-router';
import { exerciseNavTarget } from '@/lib/exercise-route';
import {
  ArrowLeft,
  BarChart2,
  Check,
  Clock,
  Home,
  Lightbulb,
  RotateCw,
  X,
  Eye,
  Play,
} from 'lucide-react';
import { SUBJECTS } from '@jeraisonne/db';
import { cn, MarkdownMath, MathText } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import { AvatarCircle } from '@/components/avatar-circle';
import { PageLoader, Spinner } from '@/components/spinner';
import { supabase } from '@/lib/supabase';
import { useLaunchPending } from '@/hooks/use-launch-pending';
import { formatDuration } from '@/components/news/format';
import { studentLinkFor } from '@/components/news/attempt-links';
import { scoreAppreciation, scoreTone, stepBarClass } from '@/components/news/attempt-score';
import { inProgressSummary } from '@/components/news/attempt-card';
import type { ViewerRole } from '@/components/news/types';
import {
  cleanAnswers,
  useAttemptReview,
  type AttemptReviewStep,
} from '@/lib/attempts/attempt-review';

const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));

function toViewerRole(role: string | null | undefined): ViewerRole {
  if (role === 'tutor' || role === 'admin') return 'tutor';
  if (role === 'parent') return 'parent';
  if (role === 'player') return 'player';
  return 'student';
}

/**
 * Résultats d'un essai question par question — ouvert depuis la carte
 * « X a terminé un exercice » du Feed. Enfant DIRECT de `appRoute` (tous rôles) ;
 * l'accès est tranché par la RPC `get_attempt_review` (migration 0272).
 */
export function AttemptReviewPage() {
  const { attemptId } = useParams({ strict: false }) as { attemptId: string };
  const { user, profile } = useAuth();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const navigate = useNavigate();
  const { data: review, isLoading, error } = useAttemptReview(attemptId);
  const { pendingId, launch } = useLaunchPending();

  // L'élève sur SON essai : refaire l'exercice (chapitre résolu à la volée).
  function retry(exerciseId: string) {
    void launch(exerciseId, async () => {
      const { data } = await supabase
        .from('exercises')
        .select('chapters ( subject, level, slug )')
        .eq('id', exerciseId)
        .maybeSingle();
      const ch = (
        data as { chapters: { subject: string; level: string; slug: string } | null } | null
      )?.chapters;
      // Exercice sans chapitre (SKILLCARD) : lecteur direct.
      void navigate(exerciseNavTarget(exerciseId, ch));
      return true;
    }).catch(console.error);
  }

  function handleBack() {
    if (canGoBack) router.history.back();
    else void navigate({ to: '/' });
  }

  const backButton = (
    <button
      type="button"
      onClick={handleBack}
      aria-label="Retour"
      className="border-ink-200 text-ink-700 hover:bg-ink-50 mb-3 flex h-11 w-11 items-center justify-center rounded-full border bg-white shadow-sm transition-colors active:scale-95"
    >
      <ArrowLeft className="h-5 w-5" />
    </button>
  );

  if (isLoading) return <PageLoader label="Chargement des résultats…" />;

  if (error || !review) {
    return (
      <div className="mx-auto max-w-sm px-4 py-6 sm:py-10">
        {backButton}
        <div className="py-10 text-center">
          <BarChart2 className="text-ink-300 mx-auto h-8 w-8" />
          <p className="text-ink-700 mt-2 font-sans text-sm font-semibold">
            Résultats indisponibles
          </p>
          <p className="text-ink-500 mt-1 font-sans text-xs">
            {error instanceof Error ? error.message : 'Cet essai est introuvable.'}
          </p>
        </div>
      </div>
    );
  }

  const studentName = review.student_name ?? 'Élève';
  const studentLink = studentLinkFor(toViewerRole(profile?.role), user?.id, review.student_id);
  const tone = scoreTone(review.score);
  const totalMs = review.steps.reduce((sum, s) => sum + (s.time_ms ?? 0), 0);
  const correctCount = review.steps.filter((s) => s.was_correct === true).length;
  const title = review.exercise_title ?? 'Exercice';
  const ownStudentAttempt = profile?.role === 'student' && user?.id === review.student_id;
  // Prof / admin : accès direct à la fiche de l'exercice (aperçu « Tester »).
  const canOpenExercise =
    !!review.exercise_id && (profile?.role === 'tutor' || profile?.role === 'admin');
  // Essai en cours ou abandonné (admin, migration 0504) : progression au lieu
  // du score.
  const abandoned = review.status === 'abandoned';
  const inProgress = review.status === 'in_progress' || abandoned;
  const remaining = inProgress
    ? Math.max(0, (review.question_count ?? review.steps.length) - review.steps.length)
    : 0;
  const shownDate = inProgress ? review.started_at : review.completed_at;
  // Son PROPRE essai en cours (quel que soit son rôle) : reprendre là où on
  // s'est arrêté. Le player retrouve la tentative et reprend à la 1re question
  // sans réponse (`planAutoResume`). Un essai abandonné ne se reprend pas.
  const canResume =
    review.status === 'in_progress' && !!review.exercise_id && user?.id === review.student_id;

  const identity = (
    <>
      <AvatarCircle
        name={studentName}
        src={review.student_avatar}
        size="lg"
        className="ocean:ring-white/10 shrink-0 ring-2 ring-white"
      />
      <div className="min-w-0 leading-tight">
        <p className="text-ink-900 truncate font-sans text-base font-semibold">{studentName}</p>
        {shownDate && (
          <p className="text-ink-500 mt-0.5 font-sans text-xs">
            {inProgress ? 'Commencé le ' : 'Terminé le '}
            {new Date(shownDate).toLocaleString('fr-FR', {
              day: 'numeric',
              month: 'long',
              hour: '2-digit',
              minute: '2-digit',
            })}
            {inProgress && (abandoned ? ' · abandonné' : ' · en cours')}
          </p>
        )}
      </div>
    </>
  );

  return (
    <div className="pb-safe mx-auto max-w-2xl px-4 py-6 sm:py-8">
      {backButton}

      <header className="border-ink-100 space-y-4 rounded-3xl border bg-white p-4 sm:p-5">
        {studentLink ? (
          <Link
            {...studentLink}
            className="-m-1 flex min-h-11 w-fit items-center gap-3 rounded-xl p-1 hover:opacity-90"
          >
            {identity}
          </Link>
        ) : (
          <div className="flex min-h-11 items-center gap-3">{identity}</div>
        )}

        <div className="flex items-center gap-4">
          <div
            className={cn(
              'flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-full border-4',
              tone.ring,
              tone.soft,
            )}
          >
            {review.score !== null ? (
              <span className={cn('font-display text-2xl font-extrabold leading-none', tone.text)}>
                {review.score}
                <span className="text-sm font-bold">%</span>
              </span>
            ) : (
              <BarChart2 className="text-ink-300 h-6 w-6" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-ink-900 text-xl font-extrabold leading-tight">
              <MathText>{title}</MathText>
            </h1>
            {inProgress ? (
              <p className="text-primary-600 ocean:text-primary-300 mt-1 font-sans text-sm font-semibold">
                {abandoned ? 'Exercice abandonné' : 'Exercice en cours'}
              </p>
            ) : (
              review.score !== null && (
                <p className={cn('mt-1 font-sans text-sm font-semibold', tone.text)}>
                  {scoreAppreciation(review.score)}
                </p>
              )
            )}
          </div>
        </div>

        {canResume && (
          <button
            type="button"
            onClick={() => retry(review.exercise_id!)}
            disabled={pendingId !== null}
            data-testid="attempt-resume"
            className="font-display bg-accent-400 hover:bg-accent-500 flex min-h-12 w-full items-center justify-center gap-2 rounded-full px-6 text-base font-bold text-[#0a2e57] shadow-md active:scale-[0.98] disabled:opacity-70 sm:w-auto"
          >
            {pendingId ? <Spinner /> : <Play className="h-5 w-5 fill-current" aria-hidden />}
            Reprendre
          </button>
        )}

        {canOpenExercise && (
          <Link
            to="/tutor/exercises/$exerciseId"
            params={{ exerciseId: review.exercise_id! }}
            className="border-ink-200 text-primary-600 hover:bg-primary-50 flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-xl border px-4 font-sans text-sm font-semibold active:scale-[0.98] sm:w-auto"
          >
            <Eye className="h-4 w-4" aria-hidden />
            Voir l’exercice
          </Link>
        )}

        <div className="flex flex-wrap gap-2 font-sans text-xs">
          {review.subject && (
            <span className="bg-ink-50 ocean:bg-white/5 text-ink-700 rounded-full px-3 py-1">
              {SUBJECT_LABEL[review.subject] ?? review.subject}
            </span>
          )}
          {inProgress && (
            <span className="bg-ink-50 ocean:bg-white/5 text-ink-700 rounded-full px-3 py-1">
              {inProgressSummary(review.steps.length, review.question_count)}
            </span>
          )}
          <span className="bg-ink-50 ocean:bg-white/5 text-ink-700 rounded-full px-3 py-1">
            {correctCount}/{review.steps.length} bonnes réponses
          </span>
          {totalMs > 0 && (
            <span className="bg-ink-50 ocean:bg-white/5 text-ink-700 flex items-center gap-1 rounded-full px-3 py-1">
              <Clock className="h-3 w-3" aria-hidden /> {formatDuration(totalMs)}
            </span>
          )}
        </div>

        {review.steps.length + remaining > 0 && (
          <nav aria-label="Aller à une question" className="flex gap-1">
            {review.steps.map((step, i) => (
              <a
                key={i}
                href={`#question-${i + 1}`}
                aria-label={`Question ${i + 1}`}
                className="flex min-h-[2.75rem] flex-1 items-center"
              >
                <span className={cn('h-2 w-full rounded-full', stepBarClass(step.was_correct))} />
              </a>
            ))}
            {Array.from({ length: remaining }, (_, i) => (
              <span key={`r${i}`} className="flex min-h-[2.75rem] flex-1 items-center" aria-hidden>
                <span className="bg-ink-100 ocean:bg-white/10 h-2 w-full rounded-full" />
              </span>
            ))}
          </nav>
        )}

        {review.is_parametric && (
          <p className="text-ink-400 font-sans text-xs italic">
            Exercice à valeurs variables : les énoncés affichés sont ceux de la version de
            référence.
          </p>
        )}
      </header>

      <ol className="mt-5 space-y-3">
        {review.steps.length === 0 ? (
          <li className="text-ink-500 py-8 text-center font-sans text-sm">
            {inProgress
              ? 'Aucune réponse pour l’instant.'
              : 'Aucune réponse enregistrée pour cet essai.'}
          </li>
        ) : (
          review.steps.map((step, i) => <StepReview key={i} step={step} index={i} />)
        )}
      </ol>

      {/* Et après ? L'élève sur son propre essai : refaire, ou revenir à ses devoirs. */}
      {ownStudentAttempt && (
        <div className="mt-6 flex flex-col gap-2 sm:flex-row" data-testid="attempt-next">
          {review.exercise_id && !canResume && (
            <button
              type="button"
              onClick={() => retry(review.exercise_id!)}
              disabled={pendingId !== null}
              className="bg-primary-500 hover:bg-primary-600 flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold text-white active:scale-[0.98] disabled:opacity-70 sm:w-auto sm:flex-1"
            >
              {pendingId ? <Spinner /> : <RotateCw className="h-4 w-4" aria-hidden />}
              Refaire l’exercice
            </button>
          )}
          <Link
            to="/student/dashboard"
            className="border-ink-200 text-ink-800 hover:bg-ink-50 flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-xl border bg-white px-4 text-sm font-semibold active:scale-[0.98] sm:w-auto sm:flex-1"
          >
            <Home className="h-4 w-4" aria-hidden />
            Retour à l’accueil
          </Link>
        </div>
      )}
    </div>
  );
}

function StepReview({ step, index }: { step: AttemptReviewStep; index: number }) {
  const chosen = cleanAnswers(step.chosen_mds);
  const correct = cleanAnswers(step.correct_mds);
  const ok = step.was_correct === true;
  const ko = step.was_correct === false;
  const numeric = step.answer_value !== null ? String(step.answer_value) : null;

  return (
    <li
      id={`question-${index + 1}`}
      className={cn(
        'border-ink-100 scroll-mt-4 rounded-2xl border border-l-4 bg-white p-4',
        ok ? 'border-l-green-400' : ko ? 'border-l-red-400' : 'border-l-ink-200',
      )}
    >
      <div className="mb-2 flex items-center gap-2">
        <span
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
            ok
              ? 'ocean:bg-green-500/20 ocean:text-green-300 bg-green-100 text-green-700'
              : ko
                ? 'ocean:bg-red-500/20 ocean:text-red-400 bg-red-100 text-red-600'
                : 'bg-ink-100 text-ink-500',
          )}
          aria-label={ok ? 'Bonne réponse' : ko ? 'Erreur' : 'Non corrigée'}
        >
          {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
        </span>
        <span className="text-ink-700 font-sans text-sm font-bold">Question {index + 1}</span>
        <span className="ml-auto flex items-center gap-2">
          {step.hint_used && (
            <span className="ocean:bg-amber-500/15 ocean:text-amber-300 flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-sans text-xs font-medium text-amber-700">
              <Lightbulb className="h-3 w-3" /> Indice
            </span>
          )}
          {step.time_ms != null && step.time_ms > 0 && (
            <span className="text-ink-400 flex items-center gap-1 font-sans text-xs">
              <Clock className="h-3 w-3" /> {formatDuration(step.time_ms)}
            </span>
          )}
        </span>
      </div>

      {step.prompt_md && (
        <div className="text-ink-900 font-sans text-[15px] leading-relaxed">
          <MarkdownMath>{step.prompt_md}</MarkdownMath>
        </div>
      )}

      <div className="mt-3 space-y-2">
        <AnswerLine
          label={ok ? 'Réponse' : 'Réponse donnée'}
          tone={ok ? 'ok' : ko ? 'ko' : 'neutral'}
          answers={numeric ? [numeric] : chosen}
          empty="Pas de réponse"
        />
        {!ok && correct.length > 0 && (
          <AnswerLine
            label={correct.length > 1 ? 'Bonnes réponses' : 'Bonne réponse'}
            tone="ok"
            answers={correct}
          />
        )}
      </div>
    </li>
  );
}

function AnswerLine({
  label,
  tone,
  answers,
  empty,
}: {
  label: string;
  tone: 'ok' | 'ko' | 'neutral';
  answers: string[];
  empty?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-xl px-3 py-2.5',
        tone === 'ok' && 'ocean:bg-green-500/15 bg-green-50',
        tone === 'ko' && 'ocean:bg-red-500/15 bg-red-50',
        tone === 'neutral' && 'bg-ink-50 ocean:bg-white/5',
      )}
    >
      <span
        className={cn(
          'mt-0.5 w-24 shrink-0 font-sans text-xs font-semibold',
          tone === 'ok' && 'ocean:text-green-300 text-green-700',
          tone === 'ko' && 'ocean:text-red-400 text-red-600',
          tone === 'neutral' && 'text-ink-500',
        )}
      >
        {label}
      </span>
      {/* Texte de la réponse en encre du thème : lisible en clair comme en sombre. */}
      <div className="text-ink-900 min-w-0 flex-1 space-y-1">
        {answers.length === 0 ? (
          <p className="font-sans text-sm italic opacity-70">{empty ?? '—'}</p>
        ) : (
          answers.map((a, i) => (
            <div key={i} className="font-sans text-[15px]">
              <MarkdownMath>{a}</MarkdownMath>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
